import { useMemo, useState } from "react";

import {
  CreditCard,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreatePlan,
  useDeletePlan,
  usePlans,
  useUpdatePlan,
} from "@/features/plans/hooks/usePlans";
import type { Plan, PlanApp, PlanPeriod } from "@/features/plans/schemas/plan";
import {
  PLAN_APP_LABEL,
  PLAN_APPS,
  PLAN_PERIOD_LABEL,
  PLAN_PERIODS,
} from "@/features/plans/schemas/plan";

type EditorMode = "idle" | "create" | "edit";

type FormState = {
  name: string;
  app: PlanApp | "";
  period: PlanPeriod;
  price: string;
  benefits: string[];
  stripeId: string;
  stripePriceId: string;
  venflowId: string;
};

const EMPTY_FORM: FormState = {
  name: "",
  app: "",
  period: "month",
  price: "",
  benefits: [],
  stripeId: "",
  stripePriceId: "",
  venflowId: "",
};

/**
 * CRUD de planes de suscripción (colección Firestore `plans`).
 */
export function PlansEditor() {
  const { data: plans = [], isLoading } = usePlans();
  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();
  const deletePlan = useDeletePlan();

  const [mode, setMode] = useState<EditorMode>("idle");
  const [editing, setEditing] = useState<Plan | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [benefitDraft, setBenefitDraft] = useState("");

  const sortedPlans = useMemo(() => [...plans], [plans]);

  const isSaving = createPlan.isPending || updatePlan.isPending;

  const resetEditor = () => {
    setMode("idle");
    setEditing(null);
    setForm(EMPTY_FORM);
    setBenefitDraft("");
  };

  const startCreate = () => {
    setMode("create");
    setEditing(null);
    setForm(EMPTY_FORM);
    setBenefitDraft("");
  };

  const startEdit = (plan: Plan) => {
    setMode("edit");
    setEditing(plan);
    setForm({
      name: plan.name,
      app: plan.app ?? "",
      period: plan.period,
      price: String(plan.price),
      benefits: [...plan.benefits],
      stripeId: plan.stripeId ?? "",
      stripePriceId: plan.stripePriceId ?? "",
      venflowId: plan.venflowId ?? "",
    });
    setBenefitDraft("");
  };

  const patchForm = (patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleAddBenefit = () => {
    const benefit = benefitDraft.trim();
    if (!benefit) return;
    if (form.benefits.includes(benefit)) {
      toast.error("Ese beneficio ya está en la lista");
      return;
    }
    patchForm({ benefits: [...form.benefits, benefit] });
    setBenefitDraft("");
  };

  const handleRemoveBenefit = (benefit: string) => {
    patchForm({ benefits: form.benefits.filter((b) => b !== benefit) });
  };

  const handleSave = async () => {
    const price = Number(form.price);
    if (
      !form.name.trim() ||
      !form.app ||
      !form.price.trim() ||
      !Number.isFinite(price)
    ) {
      toast.error("Nombre, aplicación y precio son obligatorios");
      return;
    }

    const payload = {
      name: form.name,
      app: form.app,
      period: form.period,
      price,
      benefits: form.benefits,
      stripeId: form.stripeId,
      stripePriceId: form.stripePriceId,
      venflowId: form.venflowId,
    };

    try {
      if (mode === "create") {
        await createPlan.mutateAsync(payload);
        toast.success(`Plan "${form.name}" creado`);
      } else if (mode === "edit" && editing) {
        await updatePlan.mutateAsync({ docId: editing.docId, data: payload });
        toast.success(`Plan "${form.name}" actualizado`);
      }
      resetEditor();
    } catch (error) {
      console.error("Error guardando plan:", error);
      toast.error(
        error instanceof Error ? error.message : "No se pudo guardar el plan"
      );
    }
  };

  const handleDelete = async (plan: Plan) => {
    const ok = window.confirm(`¿Eliminar el plan "${plan.name}"?`);
    if (!ok) return;

    try {
      await deletePlan.mutateAsync(plan.docId);
      toast.success(`Plan "${plan.name}" eliminado`);
      if (editing?.docId === plan.docId) resetEditor();
    } catch (error) {
      console.error("Error eliminando plan:", error);
      toast.error(
        error instanceof Error ? error.message : "No se pudo eliminar el plan"
      );
    }
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <CreditCard className="h-4 w-4" aria-hidden />
          Planes de suscripción
        </CardTitle>
        <CardDescription>
          Catálogo Firestore <code className="text-xs">plans</code>. Precios,
          beneficios y vínculo con Stripe / Venflow.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">{plans.length} plan(es)</p>
          <Button
            size="sm"
            className="cursor-pointer shrink-0"
            onClick={startCreate}
            disabled={isLoading}
          >
            <Plus className="h-4 w-4 mr-1.5" aria-hidden />
            Nuevo plan
          </Button>
        </div>

        {isLoading ? (
          <div
            className="flex justify-center py-8"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="sr-only">Cargando planes</span>
          </div>
        ) : plans.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-6">
            No hay planes. Agregá el primero.
          </p>
        ) : (
          <ul className="max-h-[min(28rem,60vh)] space-y-1.5 overflow-y-auto rounded-md border p-2">
            {sortedPlans.map((plan) => (
              <li
                key={plan.docId}
                className="flex items-start justify-between gap-2 rounded-md border px-2 py-1.5 transition-colors hover:bg-muted/40"
              >
                <div className="min-w-0 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="text-sm font-medium leading-tight">
                      {plan.name}
                    </p>
                    {plan.app ? (
                      <Badge variant="default">
                        {PLAN_APP_LABEL[plan.app]}
                      </Badge>
                    ) : (
                      <Badge variant="destructive">Sin app</Badge>
                    )}
                    <Badge variant="secondary">
                      {PLAN_PERIOD_LABEL[plan.period]}
                    </Badge>
                    <Badge variant="outline">${plan.price}</Badge>
                    {!plan.stripeId && (
                      <Badge variant="destructive">Sin Stripe</Badge>
                    )}
                    {!plan.stripePriceId && (
                      <Badge variant="destructive">Sin price id</Badge>
                    )}
                    {!plan.venflowId && (
                      <Badge variant="destructive">Sin Venflow</Badge>
                    )}
                  </div>
                  {plan.benefits.length > 0 && (
                    <p className="truncate text-[11px] text-muted-foreground">
                      {plan.benefits.join(" · ")}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-9 cursor-pointer"
                    aria-label={`Editar ${plan.name}`}
                    onClick={() => startEdit(plan)}
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-9 cursor-pointer"
                    aria-label={`Eliminar ${plan.name}`}
                    onClick={() => void handleDelete(plan)}
                    disabled={deletePlan.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog
        open={mode === "create" || mode === "edit"}
        onOpenChange={(isOpen) => !isOpen && resetEditor()}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto gap-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CreditCard className="h-4 w-4 text-muted-foreground" aria-hidden />
              {mode === "create" ? "Nuevo plan" : `Editar: ${editing?.name}`}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Plan de suscripción. Solo admin / Admin IT.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="plan-name">Nombre</Label>
                <Input
                  id="plan-name"
                  value={form.name}
                  onChange={(e) => patchForm({ name: e.target.value })}
                  placeholder="pro"
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label>Aplicación</Label>
                <Select
                  value={form.app || undefined}
                  onValueChange={(value) =>
                    patchForm({ app: value as PlanApp })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Seleccionar aplicación" />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAN_APPS.map((app) => (
                      <SelectItem key={app} value={app}>
                        {PLAN_APP_LABEL[app]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Periodo</Label>
                <Select
                  value={form.period}
                  onValueChange={(value) =>
                    patchForm({ period: value as PlanPeriod })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAN_PERIODS.map((period) => (
                      <SelectItem key={period} value={period}>
                        {PLAN_PERIOD_LABEL[period]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="plan-price">Precio</Label>
                <Input
                  id="plan-price"
                  type="number"
                  min={0}
                  value={form.price}
                  onChange={(e) => patchForm({ price: e.target.value })}
                  placeholder="120"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="plan-stripe-id">Stripe product id</Label>
                <Input
                  id="plan-stripe-id"
                  value={form.stripeId}
                  onChange={(e) => patchForm({ stripeId: e.target.value })}
                  placeholder="prod_XXXXXXXXXXXX"
                  className="font-mono text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="plan-stripe-price-id">Stripe price id</Label>
                <Input
                  id="plan-stripe-price-id"
                  value={form.stripePriceId}
                  onChange={(e) =>
                    patchForm({ stripePriceId: e.target.value })
                  }
                  placeholder="price_XXXXXXXXXXXX"
                  className="font-mono text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="plan-venflow-id">Venflow id</Label>
                <Input
                  id="plan-venflow-id"
                  value={form.venflowId}
                  onChange={(e) => patchForm({ venflowId: e.target.value })}
                  placeholder="venflow_XXXXXXXX"
                  className="font-mono text-xs"
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="plan-benefit-draft">Beneficios</Label>
                <div className="flex gap-2">
                  <Input
                    id="plan-benefit-draft"
                    value={benefitDraft}
                    onChange={(e) => setBenefitDraft(e.target.value)}
                    placeholder="Gestión de pacientes"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddBenefit();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="cursor-pointer shrink-0"
                    onClick={handleAddBenefit}
                  >
                    <Plus className="h-4 w-4 mr-1.5" aria-hidden />
                    Agregar
                  </Button>
                </div>
                {form.benefits.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Sin beneficios cargados.
                  </p>
                ) : (
                  <ul className="space-y-1 max-h-40 overflow-y-auto rounded-md border p-2">
                    {form.benefits.map((benefit) => (
                      <li
                        key={benefit}
                        className="flex items-center justify-between gap-2 rounded px-2 py-1 text-sm hover:bg-muted/50"
                      >
                        <span className="break-words">{benefit}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-8 shrink-0 cursor-pointer"
                          aria-label={`Quitar ${benefit}`}
                          onClick={() => handleRemoveBenefit(benefit)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                onClick={resetEditor}
                disabled={isSaving}
              >
                <X className="h-4 w-4 mr-1.5" aria-hidden />
                Cancelar
              </Button>
              <Button
                size="sm"
                className="cursor-pointer"
                onClick={() => void handleSave()}
                disabled={isSaving}
              >
                {isSaving && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                )}
                {mode === "create" ? "Crear plan" : "Guardar cambios"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
